import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import "./App.css";
import "@google/model-viewer";

function Ar() {
  // 設定データ
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(null);

  // ビューポートの高さを正しく設定するためのフック
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  // 現在選択されているモデル
  const [selectedModel, setSelectedModel] = useState(null);

  // ヘルプモーダルの表示状態
  const [showHelp, setShowHelp] = useState(false);

  // config.json を読み込む
  useEffect(() => {
    fetch("./config.json")
      .then((res) => {
        if (!res.ok) throw new Error("config.json の読み込みに失敗しました");
        return res.json();
      })
      .then((data) => {
        setConfig(data);
        // URL パラメータ ?id=2 またはハッシュ #/2 で初期モデルを選択
        // ?c=BK のようにコードでも指定可能（大文字小文字を区別しない）
        const params = new URLSearchParams(window.location.search);
        const paramId = params.get("id");
        const paramCode = params.get("c");
        const hashMatch = window.location.hash.match(/^#\/(\d+)$/);
        const hashId = hashMatch ? hashMatch[1] : null;
        const targetId = Number(paramId ?? hashId);
        const initial =
          (paramCode && data.models.find((m) => m.code && m.code.toUpperCase() === paramCode.toUpperCase())) ||
          (targetId && data.models.find((m) => m.id === targetId)) ||
          data.models[0];
        setSelectedModel(initial);
      })
      .catch((err) => setConfigError(err.message));
  }, []);

  // リサイズとスクロール時にビューポート高さを更新
  useEffect(() => {
    const updateViewportHeight = () => {
      setViewportHeight(window.innerHeight);
    };

    // 初期設定
    updateViewportHeight();

    window.addEventListener("resize", updateViewportHeight);
    window.addEventListener("scroll", updateViewportHeight);

    return () => {
      window.removeEventListener("resize", updateViewportHeight);
      window.removeEventListener("scroll", updateViewportHeight);
    };
  }, []);

  // ローディング・エラー表示
  if (configError) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", color: "#c00", fontSize: "16px" }}>
        設定の読み込みに失敗しました
      </div>
    );
  }
  if (!config || !selectedModel) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", color: "#333", fontSize: "16px" }}>
        読み込み中...
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        height: `${viewportHeight}px`,
        flexDirection: "column",
        alignItems: "center",
        padding: "0",
        margin: "0",
        // iOS Safariのセーフエリアを考慮
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ヘッダー */}
      <div
        style={{
          width: "100%",
          height: "60px",
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#333",
          fontSize: "20px",
          flexShrink: 0,
          borderBottom: "1px solid #ccc",
          padding: "0 12px",
          boxSizing: "border-box",
        }}
      >
        {/* 左: マスプロロゴ */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <img src={`${process.env.PUBLIC_URL}/logo_maspro.png`} alt="マスプロ" style={{ height: 28 }} />
        </div>
        {/* 中央: マスプロWebAR */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "16px",
            fontWeight: "bold",
            color: "#333",
            whiteSpace: "nowrap",
          }}
        >
          マスプロWebAR
        </div>
        {/* 右: ?アイコン+使い方 */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setShowHelp(true)}
        >
          <div
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              border: "none",
              backgroundColor: "#333",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "bold",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              lineHeight: 1,
            }}
          >
            ?
          </div>
          <span style={{ fontSize: "10px", color: "#555", marginTop: "1px" }}>使い方</span>
        </div>
      </div>

      {/* 製品名エリア */}
      <div
        style={{
          width: "100%",
          textAlign: "center",
          padding: "16px 20px 16px",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontSize: "18px",
            fontWeight: "bold",
            color: "#333",
          }}
        >
          {config.productName}{" "}
          <span
            style={{
              fontFamily: "'HelveticaLTStd-Bold', 'Helvetica Neue', Helvetica, Arial, sans-serif",
              letterSpacing: "1px",
            }}
          >
            {config.modelNumber}
          </span>
        </div>
      </div>

      {/* 3D製品画像エリア + QRコード */}
      <div
        style={{
          width: "90%",
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 0,
          position: "relative",
        }}
      >
        <model-viewer
          key={selectedModel.id}
          src={`${process.env.PUBLIC_URL}/models/${selectedModel.glb}`}
          ios-src={`${process.env.PUBLIC_URL}/models/${selectedModel.usdz}`}
          alt={selectedModel.name}
          auto-rotate
          auto-rotate-delay="0"
          rotation-per-second="30deg"
          camera-controls
          ar
          ar-modes="webxr scene-viewer quick-look"
          ar-placement="wall"
          ar-scale="auto"
          shadow-intensity="1.5"
          environment-image="neutral"
          exposure="1.2"
          shadow-softness="0"
          tone-mapping="filmic"
          contrast="110%"
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: "#ccc",
            borderRadius: "10px",
          }}
        ></model-viewer>

        {/* 種別画像・カラー画像（グレー領域内左上） */}
        {config.kind && (
          <div
            style={{
              position: "absolute",
              left: "16px",
              top: "16px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/kinds/${config.kind}`}
              alt="種別"
              className="kind-image"
            />
            {selectedModel.color && (
              <img
                src={`${process.env.PUBLIC_URL}/colors/${selectedModel.color}`}
                alt={selectedModel.name}
                className="kind-image"
              />
            )}
          </div>
        )}

        {/* QRコード（PC表示用・グレー領域内右側） */}
        <div
          style={{
            position: "absolute",
            right: "16px",
            top: "16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
          className="qr-area"
        >
          <QRCodeSVG
            value={window.location.origin + config.arPageUrl}
            size={100}
            level="M"
            style={{ border: "3px solid #333", padding: "6px", background: "#fff" }}
          />
          <div
            style={{
              textAlign: "center",
              fontSize: "12px",
              color: "#333",
              fontWeight: "bold",
              lineHeight: 1.5,
            }}
          >
            スマートフォンで
            <br />
            二次元コードを読み込んで
            <br />
            ARを試す
          </div>
        </div>
      </div>

      {/* 「製品カラーを選択」ラベル */}
      <div
        style={{
          width: "100%",
          textAlign: "center",
          padding: "8px 0 4px",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: "13px",
            color: "#333",
            fontWeight: "bold",
          }}
        >
          製品カラーを選択
        </span>
      </div>

      {/* モデル選択用サムネイル */}
      <div
        style={{
          width: "100%",
          backgroundColor: "#333",
          display: "flex",
          alignItems: "center",
          overflowX: "auto",
          padding: "8px 16px",
          flexShrink: 0,
          boxSizing: "border-box",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "12px",
            margin: "0 auto",
            minHeight: "80px",
            flexShrink: 0,
          }}
        >
          {config.models.map((model) => (
            <div
              key={model.id}
              onClick={() => {
                setSelectedModel(model);
              }}
              style={{
                width: "90px",
                minWidth: "90px",
                backgroundColor:
                  selectedModel.id === model.id ? "#aaa" : "#555",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "center",
                borderRadius: "8px",
                cursor: "pointer",
                border:
                  selectedModel.id === model.id
                    ? "1px solid #fff"
                    : "1px solid #333",
                padding: "8px 4px",
                boxSizing: "border-box",
              }}
            >
              {model.thumbnail ? (
                <img
                  src={`${process.env.PUBLIC_URL}/thumbnails/${model.thumbnail}`}
                  alt={model.name}
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "contain",
                    borderRadius: "4px",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "#999",
                    borderRadius: "4px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {model.id}
                </div>
              )}
              <span
                style={{
                  color: "#fff",
                  fontSize: "11px",
                  marginTop: "4px",
                  textAlign: "center",
                  wordBreak: "break-all",
                  maxWidth: "85px",
                }}
              >
                {model.name.split("\n").map((line, i, arr) => (
                  <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                ))}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* マスプロWebAR TOPへ戻るボタン */}
      <div
        style={{
          width: "100%",
          textAlign: "center",
          padding: "10px 24px",
          paddingBottom: "calc(10px + env(safe-area-inset-bottom))",
          flexShrink: 0,
        }}
      >
        <a
          href={config.topPageUrl}
          onClick={(e) => { if (config.topPageUrl === '#') e.preventDefault(); }}
          style={{
            display: "inline-block",
            background: "#2979ff",
            border: "none",
            borderRadius: "8px",
            color: "#fff",
            fontSize: "16px",
            fontWeight: "bold",
            padding: "12px 32px",
            cursor: "pointer",
            textDecoration: "none",
            width: "100%",
            maxWidth: "360px",
            boxSizing: "border-box",
          }}
        >
          マスプロWebAR TOPへ戻る
        </a>
      </div>
      {/* ヘルプモーダル */}
      {showHelp && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.30)",
            zIndex: 2000,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            paddingTop: "0px",
            paddingBottom: "8px",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
          onClick={() => setShowHelp(false)}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "480px",
              margin: "8px",
              marginBottom: "8px",
              backgroundColor: "rgba(0,0,0,0.80)",
              borderRadius: "16px",
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
            onClick={(e) => e.stopPropagation()}
          >
          {/* 右上の閉じるボタン */}
          <button
            onClick={() => setShowHelp(false)}
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              border: "2px solid #fff",
              backgroundColor: "transparent",
              color: "#fff",
              fontSize: "20px",
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: 0,
              zIndex: 2001,
              lineHeight: 1,
            }}
            aria-label="閉じる"
          >
            ✕
          </button>

          {/* ヘルプタイトル */}
          <h2
            style={{
              textAlign: "center",
              color: "#ffffff",
              fontSize: "22px",
              fontWeight: "bold",
              margin: "40px 0 0",
              padding: "0 20px",
            }}
          >
            マスプロWebARヘルプ
          </h2>

          {/* ヘルプ本文 */}
          <div
            style={{
              flex: 1,
              padding: "16px 24px",
              color: "#fff",
              fontSize: "15px",
              lineHeight: 1.6,
            }}
          >
            {/* 3Dモデル表示の使い方 */}
            <p
              style={{
                color: "#4dabf7",
                fontWeight: "bold",
                fontSize: "16px",
                margin: "0 0 4px",
                textAlign: "center",
              }}
            >
              ■3Dモデル表示の使い方
            </p>
            <p style={{ margin: "0 0 4px", textAlign: "center" }}>
              表示されている製品を
              <br />
              タップ、スワイプ、ピンチを操作すると、
              <br />
              移動、回転、拡大して確認できます。
              <br />
              下部の並びから異なる製品色を選択できます。
            </p>

            {/* WebARの使い方 */}
            <p
              style={{
                color: "#4dabf7",
                fontWeight: "bold",
                fontSize: "16px",
                margin: "12px 0 4px",
                textAlign: "center",
              }}
            >
              ■WebARの使い方
            </p>
            <p style={{ margin: "0 0 4px", textAlign: "center" }}>
              右下に表示されているARボタンをタップすると、
              <br />
              WebARが起動し、カメラが起動します。
            </p>
            <p
              style={{
                margin: "0 0 4px",
                textAlign: "center",
                color: "#f5c518",
              }}
            >
              右下にARボタンがない、反応しない場合は、
              <br />
              お使いの端末、WebブラウザがAR表示に未対応です。
            </p>
            <p
              style={{
                margin: "0 0 4px",
                textAlign: "center",
                color: "#ff6b6b",
              }}
            >
              スマホにカメラ利用許可などが表示される場合、
              <br />
              画面に表示される指示に従って操作してください。
            </p>
            <p style={{ margin: "0 0 4px", textAlign: "center" }}>
              カメラが起動したら、合成したい被写体に<br />スマートフォンを向けます。
              <br />
              被写体の周辺を映すようにすると、<br />製品の3Dデータが画面内に表示されます。
              <br />
              表示された3Dデータは移動、拡大、縮小することができます。
              <br />
              合成したい場所（壁面）に移動させてください。
            </p>
            <p style={{ margin: "0 0 4px", textAlign: "center" }}>
              表示画面をスクリーンショットすることで保存できます。
            </p>
            <p style={{ margin: "10px 0 4px", textAlign: "center" }}>
              （iPhone/iOSのみ）
              <br />
              画面下部の●を押すことで写真を撮影して端末に保存します。
            </p>
            <p style={{ margin: "10px 0 4px", textAlign: "center" }}>
              WebARを終了するとき、異なる製品を表示する場合は、
              <br />
              現在表示されているタブ（ウィンドウ）を閉じてください。
            </p>
          </div>

          {/* 閉じるボタン */}
          <div
            style={{
              padding: "16px 24px",
              paddingBottom: "calc(16px + env(safe-area-inset-bottom))",
              textAlign: "center",
            }}
          >
            <button
              onClick={() => setShowHelp(false)}
              style={{
                background: "none",
                border: "2px solid #fff",
                borderRadius: "8px",
                color: "#fff",
                fontSize: "18px",
                fontWeight: "bold",
                padding: "12px 32px",
                cursor: "pointer",
                width: "100%",
                maxWidth: "360px",
              }}
            >
              ✕：ヘルプを閉じる
            </button>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Ar;
